import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivateChild, UrlTree } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { DatabaseCacheService } from '../../data/cache/database-cache.service';

@Injectable()
export class UserRolesGuard implements CanActivate, CanActivateChild {

  // private accountManagementRole: boolean = false;
  // private userManagementRole: boolean = false;
  // private recordMeasurerRole: boolean = false;
  // private recordRefererRole: boolean = false;

  // private roles: string[] = null;

  constructor(
    private router: Router,
    private msalService: MsalService,
    private common: DatabaseCacheService
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    return this.checkPermission(route, state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    return this.checkPermissionChild(route, state);
  }

  
  checkPermission(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {

    // Angular can evaluate guards in the same canActivate array concurrently.
    // On a cold/private-window navigation MsalGuard may still be redirecting,
    // so do not call the user API or turn the temporary no-account state into
    // a 404. The post-login navigation will retry this route with an account.
    if (!this.ensureActiveAccount()) {
      return of(false);
    }

    return this.ensureTargetSectionContext(next).pipe(
      map(() => this.hasHomeAccess() ? true : this.notFound()),
      catchError(() => of(this.notFound()))
    );
    
  }

  hasHomeAccess(): boolean {
    const swr = this.common.getSectionWithRole();
    // Fixed: Only allow access if swr exists AND has items
    // Previous bug: swr == null was returning true
    return swr != null && swr.length > 0;
  }
  
  checkPermissionChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {

    if (!this.ensureActiveAccount()) {
      return of(false);
    }

    return this.ensureTargetSectionContext(next).pipe(
      map(() => this.checkChildRoles(state)),
      catchError(() => of(this.notFound()))
    );
  }

  private checkChildRoles(state: RouterStateSnapshot): boolean | UrlTree {

    let roles =  this.common.getSelectedSectionRoles();
    let rolesJoined = roles == null? null : roles.join(",");

    let accountManagementRole = false;
    let userManagementRole = false;
    let recordMeasurerRole = false;
    let recordRefererRole = false;

    if (rolesJoined != null && rolesJoined.length != 0) {

      if (rolesJoined.indexOf('ACCOUNT_MANAGEMENT') !== -1) {

        accountManagementRole = true;
      }

      if (rolesJoined.indexOf('USER_MANAGEMENT') !== -1) {

        userManagementRole = true;
      }

      if (rolesJoined.indexOf('MEASUREMENT') !== -1) {

        recordMeasurerRole = true;
      }

      if (rolesJoined.indexOf('REFER') !== -1) {

        recordRefererRole = true;
      }

      // Allow legal pages without role check
      if (state.url.includes('/tou') || state.url.includes('/privacy') || state.url.includes('/license')) {
        return true;
      }

      // check admin section rights
      // Note: URLs include sectionId like /section-admin/:sectionId/staff-list
      // so we use includes() instead of exact match
      if (state.url.startsWith('/section-admin')) {
        if (!accountManagementRole && !userManagementRole) {
          // High security: return 404 for all admin errors (prevents route enumeration)
          return this.notFound();
        }

        if (!accountManagementRole) {
          if (state.url.includes('/staff-list')
            || state.url.includes('/staff-invite')
            || state.url.includes('/contributor-invite')
            || state.url.includes('/supervisor-invite')
            || state.url.includes('/staff-details')) {
              // High security: return 404 for all admin errors (prevents route enumeration)
              return this.notFound();
          }
        }

        if (!userManagementRole) {
          if (state.url.includes('/user-list')
            || state.url.includes('/create-user')
            || state.url.includes('/user-details')) {
            // High security: return 404 for all admin errors (prevents route enumeration)
            return this.notFound();
          }
        }
      }

    } else {
      // High security: return 404 for all admin errors (prevents route enumeration)
      return this.notFound();
    }

    return true;
  }

  /**
   * A direct admin URL can run before the selected-section cache is ready.
   * Reuse the cached login response when possible; otherwise load the user and
   * select the section from the URL before checking its roles.
   */
  private ensureTargetSectionContext(route: ActivatedRouteSnapshot): Observable<void> {
    const sectionId = this.extractSectionId(route);
    if (!sectionId) {
      if (this.hasHomeAccess()) {
        return of(undefined);
      }

      return from(this.common.setLoginUserAndSelectedSection()).pipe(
        switchMap(response => {
          if (!response?.sectionWithRole?.length) {
            throw new Error('No admin section is available to the current user.');
          }
          return of(undefined);
        })
      );
    }

    const selectedSectionId = this.common.getSelectedSection()?.sectionId;
    const selectedRoles = this.common.getSelectedSectionWithRole();
    if (selectedSectionId === sectionId && selectedRoles?.sectionId === sectionId) {
      return of(undefined);
    }

    return from(this.common.setLoginUserAndSelectedSection(sectionId)).pipe(
      switchMap(response => {
        const hasTargetSection = response?.sectionWithRole?.some(
          (section: { sectionId: string }) => section.sectionId === sectionId
        );
        const targetIsSelected = this.common.getSelectedSection()?.sectionId === sectionId;

        if (!hasTargetSection || !targetIsSelected) {
          throw new Error('The requested section is not available to the current user.');
        }

        return of(undefined);
      })
    );
  }

  private extractSectionId(route: ActivatedRouteSnapshot): string | null {
    let snapshot: ActivatedRouteSnapshot | null = route;
    while (snapshot) {
      const sectionId = snapshot.paramMap.get('sectionId');
      if (sectionId) {
        return sectionId;
      }
      snapshot = snapshot.parent;
    }

    const descendants = [...(route.children ?? [])];
    while (descendants.length > 0) {
      const child = descendants.shift();
      const sectionId = child?.paramMap.get('sectionId');
      if (sectionId) {
        return sectionId;
      }
      if (child) {
        descendants.push(...child.children);
      }
    }

    return null;
  }

  private notFound(): UrlTree {
    return this.router.createUrlTree(['/not-found']);
  }

  private ensureActiveAccount(): boolean {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount?.idTokenClaims?.['sub']) {
      return true;
    }

    const account = this.msalService.instance.getAllAccounts()
      .find(candidate => !!candidate.idTokenClaims?.['sub']);
    if (!account) {
      return false;
    }

    this.msalService.instance.setActiveAccount(account);
    return true;
  }

}
